import React, { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader } from '@fluentui/react-northstar';
import useWindowWidth from '../../useWindowWidth';
import { reachedScrollBottom } from '../../../shared/utils/scrollBottom';
import MgtCss from '../../../mgt.module.css';

// As the ticket board's VirtualLaneBody: rows are measured once drawn, a few beyond the screen are kept, and the next
// page is asked for once the lane is scrolled within one card of its bottom.
const ESTIMATED_ROW_HEIGHT = 200;
const OVERSCAN = 4;

interface Props<T> {
  items: T[];
  keyOf: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  /** Shown at the foot of the lane while the next page loads - on desktop. A phone shows the spinner instead. */
  loadingRows: React.ReactNode[];
}

// A My Work board lane's cards, drawn only while in view, in the lane's own scroll area (which spans the lane, so the
// scrollbar sits at its edge). Each card keeps the lane's 17px side padding and desktop width cap (myWorkLaneCard).
export const VirtualLane = <T,>(props: Props<T>) => (useWindowWidth() < 768 ? <PhoneLane {...props} /> : <DesktopLane {...props} />);

const DesktopLane = <T,>({ items, keyOf, renderItem, hasMore, isLoadingMore, onLoadMore, loadingRows }: Props<T>) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const extraRows = isLoadingMore ? loadingRows.length : 0;

  const virtualizer = useVirtualizer({
    count: items.length + extraRows,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: index => (index < items.length ? String(keyOf(items[index])) : `__loading_${index - items.length}`),
  });
  const scrollOffset = virtualizer.scrollOffset ?? 0;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLoadingMore) return;
    // Only once the user has scrolled: before the rows are measured, scrollHeight equals clientHeight.
    if (el.scrollTop <= 0) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= ESTIMATED_ROW_HEIGHT) onLoadMore();
  }, [scrollOffset, hasMore, isLoadingMore, items.length, onLoadMore]);

  return (
    <div ref={scrollRef} className='my-work-lane-content' style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map(row => (
          <div
            key={row.key as React.Key}
            data-index={row.index}
            ref={virtualizer.measureElement}
            // flow-root keeps a card's bottom margin inside the row, so it is measured and cards stay apart.
            style={{ position: 'absolute', top: row.start, left: 0, width: '100%', padding: '0 17px', boxSizing: 'border-box', display: 'flow-root' }}
          >
            <div className={MgtCss.myWorkLaneCard}>
              {row.index < items.length ? renderItem(items[row.index]) : loadingRows[row.index - items.length]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Below Tailwind's md, a lane is drawn the way the ticket list's phone cards are (MobileList): every loaded card,
// plainly, in the page's one scroll area (my-work-phone-body) - no scroll area of its own and no virtual list - and the
// next page loads once that area reaches its bottom, with a spinner under the last card.
const PhoneLane = <T,>({ items, keyOf, renderItem, hasMore, isLoadingMore, onLoadMore }: Props<T>) => {
  const laneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scroller = laneRef.current?.closest('.my-work-phone-body');
    if (!scroller) return;
    const onScroll = () => {
      // Every lane is mounted and the dropdown shows one; the others are display: none, with no offsetParent.
      if (!hasMore || isLoadingMore || !laneRef.current?.offsetParent) return;
      if (reachedScrollBottom(scroller)) onLoadMore();
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div ref={laneRef} className='my-work-lane-content' style={{ padding: '0 17px' }}>
      {items.map(item => <div key={keyOf(item)} style={{ display: 'flow-root' }}>{renderItem(item)}</div>)}
      {isLoadingMore && <div className='py-3'><Loader /></div>}
    </div>
  );
};

// The lane's skeleton, empty and upgrade states: the same padding and width cap as the cards, without a scroll area.
export const LaneFrame: React.FC = ({ children }) => (
  <div className='my-work-lane-content' style={{ height: '100%', padding: '0 17px' }}>
    <div className={MgtCss.myWorkLaneCard} style={{ height: '100%' }}>{children}</div>
  </div>
);
